import { memo, useEffect, useState, useRef } from 'react';

import { DropDownList, DropDownListLi } from './drop-down-list.styles';
import DropDownItemComponent from '../drop-down-item/drop-down-item.component';
import { SelectOption } from '../../types/config';

import sanitiseString from '../../lib/utils/sanitise-string';

interface DropDownListComponentProps {
	items: SelectOption[];
	onSelect: (option: SelectOption) => void;
	isOpen: boolean;
	itemIcon?: React.ReactElement;
	clearSelection?: boolean;
}

const DropDownListComponent = ({
	items,
	onSelect,
	isOpen = false,
	itemIcon,
	clearSelection
}: DropDownListComponentProps) => {
	const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
	const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

	const selected = items?.find((item) => item?.selected === true);
	const [selectedValue, setSelectedValue] = useState<string | number>(
		selected?.value || ''
	);

	const handleSelection = (option: SelectOption) => {
		onSelect(option);
		setSelectedValue(option.value);
	};

	const handleKeyDown = (event: KeyboardEvent) => {
		const { key } = event;
		if (focusedIndex === null) return;

		switch (key) {
			case 'ArrowDown':
				const nextIndex = (focusedIndex + 1) % items.length;
				setFocusedIndex(nextIndex);
				optionRefs.current[nextIndex]?.focus();
				break;

			case 'ArrowUp':
				const prevIndex = (focusedIndex - 1 + items.length) % items.length;
				setFocusedIndex(prevIndex);
				optionRefs.current[prevIndex]?.focus();
				break;

			default:
				break;
		}
	};

	useEffect(() => {
		if (isOpen) {
			setFocusedIndex(-1);
		} else {
			setFocusedIndex(null);
		}
	}, [isOpen]);

	useEffect(() => {
		if (isOpen) {
			window.addEventListener('keydown', handleKeyDown);
		}

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [isOpen, focusedIndex]);

	useEffect(() => {
		if (clearSelection) {
			setSelectedValue('');
		}
	}, [clearSelection]);

	return (
		<DropDownList data-cy="drop-down-list-component" role="listbox">
			{items.map((item, index) => (
				<DropDownListLi key={`dropdown-${sanitiseString(item.label)}`}>
					<DropDownItemComponent
						option={{
							label: item.label,
							value: item.value,
							selectedLabel: item.selectedLabel,
							selected: selectedValue === item.value
						}}
						onSelect={(option: SelectOption) => handleSelection(option)}
						isActive={isOpen}
						ref={(el) => (optionRefs.current[index] = el)}
						itemIcon={itemIcon}
					/>
				</DropDownListLi>
			))}
		</DropDownList>
	);
};

export default memo(DropDownListComponent);
